import type { ComponentType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Kartochkaning ohangi.
 *
 * `warning` — "bu yerda kutib turgan ish bor": kartochka chegarasi va belgisi ogohlantirish
 * rangiga o'tadi. `muted` esa teskarisi — navbat bo'sh, kartochka fonga chekinadi. Ohang
 * RAQAMDAN olinadi (nol yoki noldan katta), matn bilan takrorlanmaydi: bir xil gapni ikki
 * marta aytish o'rniga rang uni raqamni o'qishdan oldin yetkazadi.
 */
export type StatTone = 'default' | 'warning' | 'muted';

const TONE_CARD: Record<StatTone, string> = {
  default: '',
  warning: 'border-warning/55 bg-warning/[0.05]',
  muted: 'opacity-65',
};

const TONE_CHIP: Record<StatTone, string> = {
  default: 'bg-primary/8 text-primary/75 dark:bg-primary/12',
  warning: 'bg-warning text-warning-foreground',
  muted: 'bg-muted text-muted-foreground',
};

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  hint?: ReactNode;
  tone?: StatTone;
  className?: string;
}

/**
 * Bitta raqamli kartochka.
 *
 * Raqam SERVERDAN keladi, ekrandagi ro'yxatdan sanalmaydi. Ilgari teskarisi edi va o'sha paytda
 * bu to'g'ri qaror bo'lgan: ro'yxatlar to'liq kelardi, ya'ni sanoq ular bilan bir xil haqiqatni
 * ko'rsatardi. Sahifalash kiritilgach shu qoida jimgina yolg'onga aylandi — "jami 4 ta efir"
 * aslida "shu sahifada 4 ta" degan ma'noni olardi, ekranda esa hech narsa o'zgarmasdi.
 *
 * Belgi (ikonka) yorliq yonida emas, o'ng burchakda alohida maydonchada: kartochkadagi eng
 * muhim narsa — RAQAM, va u chap chekkadan boshlanishi kerak. Yorliq oldidagi belgi raqamni
 * bir necha piksel o'ngga surib, ustma-ust turgan kartochkalar qatorida raqamlar chizig'ini
 * buzardi.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  tone = 'default',
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        'group bg-card motion-lift border-border/70 relative overflow-hidden rounded-xl border px-4 py-3.5',
        TONE_CARD[tone],
        className,
      )}
    >
      {/*
        Sichqoncha ostida yuqori chekkada ingichka brend chizig'i paydo bo'ladi. U hech qanday
        ma'no bermaydi va bermasligi ham kerak — vazifasi shundaki, kursor ostidagi kartochka
        qo'shnilaridan ajralib tursin (ular yonma-yon va bir xil).
      */}
      <span
        aria-hidden="true"
        className="from-primary/0 via-primary to-primary/0 absolute inset-x-0 top-0 h-px scale-x-0 bg-gradient-to-r opacity-0 transition-all duration-(--dur-control) ease-(--ease-out-soft) group-hover:scale-x-100 group-hover:opacity-100"
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {label}
          </div>

          {/*
            Kalit sifatida QIYMATNING O'ZI berilgan: son o'zgarganda React bu tugunni almashtiradi
            va CSS animatsiyasi qaytadan o'ynaydi. Ya'ni "yangilandi" ni ko'rsatish uchun na
            taymer, na holat kerak — o'zgarish faktining o'zi harakatni keltirib chiqaradi.
            Qiymat matn bo'lmasa (masalan yorliq yoki chiziqcha) kalit berilmaydi va kartochka
            jim turadi.
          */}
          <div
            key={typeof value === 'string' || typeof value === 'number' ? String(value) : undefined}
            className="motion-value tabular mt-1.5 text-2xl leading-none font-semibold tracking-tight"
          >
            {value}
          </div>

          {hint ? <div className="text-muted-foreground mt-1.5 text-xs">{hint}</div> : null}
        </div>

        {Icon ? (
          <span
            aria-hidden="true"
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-lg transition-all duration-(--dur-control) ease-(--ease-spring) group-hover:-rotate-6 group-hover:scale-110',
              TONE_CHIP[tone],
            )}
          >
            <Icon className="size-4" aria-hidden />
          </span>
        ) : null}
      </div>
    </div>
  );
}
